import { useLatest } from 'ahooks';
import cloneDeep from 'lodash/cloneDeep';
import React, { useEffect, useRef, useState } from 'react';
import { Modal, Tooltip } from '@meitu/saas-ui';
import { clientInfo } from '@meitu/pc-bridge';
import IconFont from '@meitu/xdesign-icon-font';
import { BaseOperateView, BaseStagePlugin, IFramework } from '@/framework';
import { useLocaleStore } from '@/i18n/index';
import { IImageLayerProps, ILayerProps } from '@/version-types';
import { getCutoutOperateLayer } from '../operate-layer';
import { getImageCutoutInfo, useImageCutoutState } from '../states/image-cutout-state';
import './image-cutout.less';

const SPACE = 35;

interface IProps {
    isFree: boolean;
    // 是否静默扣除场景（需要正常扣除张数，但是不需要相关提示），目前主要用在企业版场景下
    isSilentMode: boolean;
    numLeft: number;
    onCancel: Function;
    onOptimize: Function;
    onConfirm: Function;
    beforeConfirm: Function;
    isIntl?: boolean;
    // 针对是国内web且非会员无张数用户
    isInternalUserAndNotVipAndNotLeftNum?: boolean;
    // 是否为国内 web 端会员
    isInternalUserVip?: boolean;
    leftFreeNum?: number;
    freeNum?: number;
    hideCutoutOptimize?: boolean;
    framework: IFramework;
    trackFunc?: string;
}

const ImageCutout: React.FC<IProps> = ({
    isFree,
    isSilentMode,
    numLeft = 0,
    onCancel,
    onOptimize,
    onConfirm,
    beforeConfirm,
    isIntl,
    isInternalUserAndNotVipAndNotLeftNum,
    isInternalUserVip,
    leftFreeNum = 0,
    freeNum = 0,
    hideCutoutOptimize,
    framework,
    trackFunc,
}) => {
    const { $t } = useLocaleStore();

    const imageCutoutInfo = useImageCutoutState()((state) => state.imageCutoutInfo);
    const setImageCutoutInfo = useImageCutoutState()((state) => state.setImageCutoutInfo);
    const setIsImageCutout = useImageCutoutState()((state) => state.setIsImageCutout);
    const isImageCutout = useImageCutoutState()((state) => state.isImageCutout);
    const { originImage, resultImage, isCutoutMainLayer } = imageCutoutInfo;
    const [activeLayer, setActiveData] = useState<ILayerProps | null>(framework.service.frame.getActivePickLayer());
    const [activeLayerId, setActiveDataId] = useState<string | null>(
        framework.service.frame.getActivePickLayer()?.id || null,
    );
    const originLayerDataRef = useRef<ILayerProps | null>(null);

    useEffect(() => {
        const handle = framework.service.frame.onActiveLayerDataUpdate((layer, pickLayer) => {
            setActiveDataId(pickLayer?.id || layer?.id || null);
        });

        return () => {
            framework.service.events.off(handle);
        };
    }, []);

    useEffect(() => {
        if (activeLayerId) {
            const layer = framework.service.flatLayer.getLayer(activeLayerId, true);
            setActiveData(layer);
        } else {
            if (originLayerDataRef.current) {
                setActiveData(null);
                originLayerDataRef.current = null;
                resetImageCutoutInfo();
                setIsImageCutout(false);
                framework.service.editor.setDisableOperate(false);
            }
        }
    }, [activeLayerId]);

    const consumePrice = 1;

    const resetImageCutoutInfo = () => {
        setImageCutoutInfo(getImageCutoutInfo());
    };
    const cancelLogic = () => {
        setIsImageCutout(false);
        resetImageCutoutInfo();
        if (!originLayerDataRef.current) return;
        if (framework.service.flatLayer.getLayer(originLayerDataRef.current.id)) {
            framework.service.frame.addOrUpdateLayer(cloneDeep(originLayerDataRef.current));
            framework.service.frame.setActiveLayer(originLayerDataRef.current.id);
        }
        originLayerDataRef.current = null;
    };

    const handleCancel = async () => {
        if (onCancel) await onCancel();
        cancelLogic();
        framework.service.editor.setDisableOperate(false);
        framework.service.mask.hide();
    };

    const handleConfirm = async () => {
        if (onConfirm) onConfirm();
        const oldInfo = cloneDeep(originLayerDataRef.current);
        if (oldInfo) {
            const newInfo = framework.service.flatLayer.getLayer(oldInfo.id, true);
            if (newInfo) {
                const newInfoData = newInfo.data as IImageLayerProps;
                const oldInfoData = oldInfo.data as IImageLayerProps;
                newInfoData.url = resultImage;
                oldInfoData.url = originImage;
                framework.service.frame.addOrUpdateLayer(newInfo);
                framework.service.history.write(oldInfo, newInfo, trackFunc);
            }
        }
        resetImageCutoutInfo();
        originLayerDataRef.current = null;
        framework.service.editor.setDisableOperate(false);
        framework.service.mask.hide();
    };

    const checkConfirm = useLatest(() => {
        if (isFree && !isCutoutMainLayer) {
            handleConfirm();
            return;
        }

        if (clientInfo.isClient) {
            // PC内嵌
            checkConfirmInPc();
            return;
        }

        Modal.confirm({
            title: `${$t((v) => v.imageMatting.confirmText)} ${isIntl ? '' : '？'}`,
            content: isIntl ? (
                <span className="c-image-cutout-modal__content">{$t((v) => v.imageMatting.applyTips)}</span>
            ) : !isSilentMode && !isInternalUserAndNotVipAndNotLeftNum && !isInternalUserVip ? (
                <div>
                    <span className="c-image-cutout-modal__content">
                        {$t((v) => v.imageMatting.consumeAfterApply)}：{consumePrice}
                    </span>
                    <span>
                        ({$t((v) => v.imageMatting.remainPage)}：{numLeft})
                    </span>
                </div>
            ) : null,
            hideIcon: true,
            closable: false,
            maskClosable: false,
            className: 'c-image-cutout-modal',
            centered: true,
            okText:
                !isIntl && !numLeft && !isInternalUserAndNotVipAndNotLeftNum && !isInternalUserVip
                    ? $t((v) => v.imageMatting.recharge)
                    : undefined,
            onOk: async () => {
                framework.service.mask.show({ loading: true });
                const status = await beforeConfirm('ok');
                // 校验不通过，则保持当前状态
                if (status !== undefined && !status) {
                    if (originLayerDataRef.current?.id) {
                        framework.service.frame.setActiveLayer(originLayerDataRef.current.id);
                    }
                    framework.service.mask.hide();
                    return;
                }
                handleConfirm();
            },
            onCancel: () => {
                handleCancel();
            },
        });
    });

    const handleApply = async () => {
        if (isFree) {
            handleConfirm();
            return;
        }

        if (clientInfo.isClient) {
            // PC内嵌
            checkConfirmInPc();
            return;
        }

        framework.service.mask.show({ loading: true });

        const status = await beforeConfirm('apply');
        // 校验不通过，则保持当前状态
        if (status !== undefined && !status) {
            framework.service.mask.hide();
            return;
        }
        handleConfirm();
    };

    // PC内嵌场景下抠图的确认弹窗逻辑
    const checkConfirmInPc = () => {
        Modal.confirm({
            title: leftFreeNum ? '智能抠图限时免费试用' : '开通会员抠图不受限',
            content: (
                <div>
                    活动期间，您可享有{freeNum}
                    次智能抠图限免次数，是否消耗限免次数？
                    <div style={{ marginTop: 20 }}>
                        累计已消耗：{freeNum - leftFreeNum}/{freeNum}
                    </div>
                </div>
            ),
            centered: true,
            closable: false,
            maskClosable: false,
            okText: !leftFreeNum ? '立即开通' : undefined,
            onOk: async () => {
                const status = await beforeConfirm('ok');
                // 校验不通过，则保持当前状态
                if (status !== undefined && !status) {
                    if (originLayerDataRef.current?.id) {
                        framework.service.frame.setActiveLayer(originLayerDataRef.current.id);
                    }
                    return;
                }
                handleConfirm();
            },
            onCancel: () => {
                handleCancel();
            },
        });
    };

    // 抠图优化
    const handleOptimize = async () => {
        // sept1: 进入优化
        onOptimize();

        // step2: 取消应用
        cancelLogic();
    };

    useEffect(() => {
        if (!isImageCutout || !originImage || originLayerDataRef.current) return;
        const cutoutLayer = isCutoutMainLayer
            ? framework.service.frame.getMainLayer(framework.service.frame.getActiveFrameId())
            : activeLayer;
        if (!cutoutLayer) return;

        // 操作栏在预览开始后才挂载，此时图层可能已替换为结果图；原图快照只初始化一次。
        const originLayer = cloneDeep(cutoutLayer);
        (originLayer.data as IImageLayerProps).url = originImage;
        originLayerDataRef.current = originLayer;
    }, [activeLayer, framework.service.frame, isImageCutout, isCutoutMainLayer, originImage]);

    useEffect(() => {
        // 失焦抠图确认
        const doConfirm = (e: MouseEvent) => {
            const targetEl = e.target as HTMLElement;
            // 确认弹窗点击
            if (
                document.querySelector('.c-image-cutout-buttons')?.contains(targetEl) || // 点击取消/应用面板
                document.querySelector('.c-image-cutout-tooltip')?.contains(targetEl) || // 点击取消/应用面板
                document.querySelector('.buy-vip-modal')?.contains(targetEl) || // 点击订阅弹窗蒙层
                document.querySelector('.saas-subscription-popup')?.contains(targetEl) || // 点击订阅弹窗
                document.querySelector('.x-design-subscribe-popup-mask')?.contains(targetEl) || // 点击海外订阅蒙层
                document.querySelector('.x-design-subscribe-popup')?.contains(targetEl) || // 点击海外订阅弹窗
                document.querySelector('.starii-account-view')?.contains(targetEl) || // 点击海外登录弹窗
                document.querySelector('.xdesign-modal')?.contains(targetEl) || // 点击海外弹窗
                (document.querySelector('.aiMatting-Adjust')?.contains(targetEl) &&
                    !targetEl.classList.contains('matting-confirm') &&
                    !targetEl.parentElement?.classList.contains('matting-confirm')) || // 点击调整智能抠图
                document.querySelector('#extension-container')?.contains(targetEl) || // 点击 SDK 弹窗
                document.querySelector('.matting-plugin-view')?.contains(targetEl) || // 点击抠图 SDK 视图
                document.querySelector('.mask-loading-wrap')?.contains(targetEl) || // 点击 loading 蒙层
                Array.from(document.querySelectorAll('.p-editor__mask')).find((ele) => ele?.contains(targetEl)) || // 点击 mask 蒙层
                Array.from(document.querySelectorAll('.ant-modal-root')).find((ele) => ele?.contains(targetEl)) // 点击确认弹窗模块
            ) {
                return;
            }
            // console.log('ot cutout', { isImageCutout, resultImage });
            if (isImageCutout && resultImage) {
                checkConfirm.current?.();
            }
        };

        document.addEventListener('mousedown', doConfirm, true);
        return () => {
            document.removeEventListener('mousedown', doConfirm, true);
        };
    }, [isImageCutout, resultImage]);

    const renderTooltipContent = () => (
        <div className="c-image-cutout-tooltip__body">
            <div className="c-image-cutout-tooltip__info">
                <span>
                    {$t((v) => v.imageMatting.cutoutRemainPage)}：{numLeft}
                </span>
                <span className="c-image-cutout-tooltip__line"></span>
                <span>
                    {$t((v) => v.imageMatting.consumeAfterApply)}：{consumePrice}
                </span>
            </div>
            <div className="c-image-cutout-tooltip__desc">{$t((v) => v.imageMatting.remainPageConfirm)}</div>
        </div>
    );

    if (!isImageCutout || !resultImage || !activeLayer || isCutoutMainLayer) return null;

    return (
        <BaseStagePlugin enablePointerEvents style={{ zIndex: 20 }} name="image-cutout">
            <BaseOperateView
                layer={getCutoutOperateLayer(framework, activeLayer)}
                isEdgelessCoord
                style={{ pointerEvents: 'none' }}
            >
                <div className="c-image-cutout-buttons" style={{ top: -SPACE }}>
                    <div className="c-image-cutout-btn" onClick={() => handleCancel()}>
                        {$t((v) => v.imageMatting.cancel)}
                    </div>
                    <div className="c-image-cutout-btn c-image-cutout-confirm">
                        <span onClick={handleApply}>{$t((v) => v.imageMatting.apply)}</span>
                        {!isIntl &&
                        !isFree &&
                        !isInternalUserAndNotVipAndNotLeftNum &&
                        !clientInfo.isClient &&
                        !isSilentMode ? (
                            <>
                                <span className="c-image-cutout-tips" onClick={handleApply}>
                                    （{$t((v, { r }) => r(v.imageMatting.consumeOne, [consumePrice]))}）
                                </span>
                                <Tooltip
                                    placement="top"
                                    overlayClassName="c-image-cutout-tooltip"
                                    title={renderTooltipContent()}
                                >
                                    <span>
                                        <IconFont name="InfoCircleBold" color="#939599" fontSize={14} />
                                    </span>
                                </Tooltip>
                            </>
                        ) : null}
                    </div>
                    {!hideCutoutOptimize ? (
                        <div className="c-image-cutout-btn" onClick={() => handleOptimize()}>
                            {$t((v) => v.imageMatting.mattingOptimize)}
                        </div>
                    ) : null}
                </div>
            </BaseOperateView>
        </BaseStagePlugin>
    );
};

export default React.memo(ImageCutout);
